import axios from "axios";
import CONFIG from "../config";

// Events other parts of the app listen for.
export const FORCE_LOGOUT_EVENT = "forceLogout";
export const TOKEN_REFRESHED_EVENT = "tokenRefreshed";

// `/auth/refresh` only exists on core-service, even for the Flask / customer
// clients — always refresh against EMPLOYEE_SERVICE_URL.
const REFRESH_URL = `${CONFIG.EMPLOYEE_SERVICE_URL}/auth/refresh`;

// How many times to retry a *transient* refresh failure (network / 5xx / timeout)
// before giving up. We never force-logout on these — the session is still valid.
const MAX_TRANSIENT_RETRIES = 3;
const REFRESH_TIMEOUT_MS = 15000;

// Singleton in-flight refresh. All callers (the 401 interceptor across every
// axios client + the proactive scheduler) await the same promise, so a burst of
// 401s coalesces into a single `/auth/refresh` call.
let refreshPromise = null;

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// A 4xx from the refresh endpoint means the refresh token itself was rejected
// (invalid / expired / missing) — the session is genuinely over. Anything else
// (no response = network error, or a 5xx = server/gateway blip) is transient:
// the refresh token is probably still good, so we retry and keep the session.
const isFatalStatus = (status) => status >= 400 && status < 500;

const backoff = (attempt) => Math.min(500 * 2 ** (attempt - 1), 4000);

async function doRefresh() {
  const refreshToken = localStorage.getItem("refreshToken");
  if (!refreshToken) {
    const err = new Error("No refresh token");
    err.fatal = true;
    throw err;
  }

  let attempt = 0;
  let lastErr;
  while (attempt <= MAX_TRANSIENT_RETRIES) {
    try {
      // Naked axios (no interceptors) so a 401 here can't recurse into refresh.
      const res = await axios.post(
        REFRESH_URL,
        { refreshToken },
        { timeout: REFRESH_TIMEOUT_MS }
      );
      const newAccessToken = res.data?.token;
      const newRefreshToken = res.data?.refreshToken;
      if (!newAccessToken) {
        const err = new Error("Refresh response missing token");
        err.fatal = true;
        throw err;
      }
      localStorage.setItem("token", newAccessToken);
      // The backend rotates the refresh token on every call with a fresh 7-day
      // expiry, so persisting it here is what makes the session "slide" — an
      // active user keeps getting a new 7-day window and never lapses.
      if (newRefreshToken) {
        localStorage.setItem("refreshToken", newRefreshToken);
      }
      return newAccessToken;
    } catch (err) {
      lastErr = err;
      const status = err.response?.status;
      if (err.fatal || (status != null && isFatalStatus(status))) {
        err.fatal = true;
        throw err;
      }
      // Transient — back off and retry.
      attempt += 1;
      if (attempt > MAX_TRANSIENT_RETRIES) break;
      await delay(backoff(attempt));
    }
  }
  // Exhausted transient retries. Do NOT end the session — let the caller fail
  // and the user retry; a later request (or the proactive scheduler) will heal.
  lastErr.fatal = false;
  throw lastErr;
}

/**
 * Refresh the access token, coalescing concurrent callers into one request.
 * Resolves with the new access token. Rejects on failure; only *fatal* failures
 * (refresh token rejected/absent) dispatch `forceLogout`.
 */
export function refreshAccessToken() {
  if (!refreshPromise) {
    refreshPromise = doRefresh()
      .then((token) => {
        window.dispatchEvent(
          new CustomEvent(TOKEN_REFRESHED_EVENT, { detail: { token } })
        );
        return token;
      })
      .catch((err) => {
        if (err.fatal) {
          window.dispatchEvent(new Event(FORCE_LOGOUT_EVENT));
        }
        throw err;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
}

/**
 * Read the access token's expiry in epoch-ms, or null if unreadable.
 *
 * This decodes ONLY the `exp` field for refresh *timing*. It is not a trust
 * decision — identity/authz still comes solely from the capabilities endpoint,
 * never from decoded JWT claims (see CLAUDE.md).
 */
export function getAccessTokenExpiry() {
  const token = localStorage.getItem("token");
  if (!token) return null;
  try {
    const part = token.split(".")[1];
    if (!part) return null;
    const json = atob(part.replace(/-/g, "+").replace(/_/g, "/"));
    const payload = JSON.parse(json);
    return typeof payload.exp === "number" ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
}
